import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import api, { AUTH_LOGOUT_EVENT, authDebug, markAuthenticated, refreshAccessToken } from '../lib/api';

const AuthContext = createContext(null);

const LEGACY_TO_ACCESS = {
  Admin: 'Super Admin',
  'Sales Head': 'Manager',
  'Sales Executive': 'Editor',
  'Sales Intern': 'Editor',
};

const ROLE_KEY_LABELS = { SUPER_ADMIN: 'Super Admin', ADMIN: 'Admin', MANAGER: 'Manager', EDITOR: 'Editor' };

/** Pure access derivation — prefers the server-derived Phase 1 roleKey, falls back to legacy labels. */
const deriveAccess = (user, rbac) => {
  if (!user) return { accessRole: null, roleKey: null, isSuperAdmin: false, isManager: false };
  const roleKey = rbac?.roleKey || (user.roleKey) || null;
  const accessRole = (roleKey && ROLE_KEY_LABELS[roleKey]) || user.access_role || LEGACY_TO_ACCESS[user.role] || 'Editor';
  const effectiveKey = roleKey || Object.keys(ROLE_KEY_LABELS).find((k) => ROLE_KEY_LABELS[k] === accessRole) || 'EDITOR';
  return {
    accessRole,
    roleKey: effectiveKey,
    isSuperAdmin: effectiveKey === 'SUPER_ADMIN',
    isManager: ['SUPER_ADMIN', 'ADMIN', 'MANAGER'].includes(effectiveKey),
  };
};

/** Pure permission check against the (derived) legacy module matrix. */
const checkPerm = ({ user, permissions, isSuperAdmin }, module, action) => {
  if (!user) return false;
  if (isSuperAdmin) return true;
  if (permissions) return Boolean(permissions[module] && permissions[module][action]);
  return user.role === 'Admin' || user.role === 'Sales Head';
};

/** Pure feature-level check against the server-derived grant map ("MODULE.feature" -> [actions]). */
const checkFeature = ({ user, rbac, isSuperAdmin }, moduleKey, featureKey, action = 'view') => {
  if (!user) return false;
  if (isSuperAdmin) return true;
  const grants = rbac?.features?.[`${moduleKey}.${featureKey}`];
  return Array.isArray(grants) && grants.includes(action);
};

/**
 * Explicit session states:
 *   'loading'         initial cookie check (+ one refresh attempt) still running — never redirect
 *   'authenticated'   user object present
 *   'unauthenticated' conclusively signed out (no cookie / refresh failed / explicit logout)
 *   'error'           server unreachable during the initial check — show retry, keep cookies, never redirect
 */
export const AuthProvider = ({ children }) => {
  const [status, setStatus] = useState('loading');
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [rbac, setRbac] = useState(null);
  const [logoutReason, setLogoutReason] = useState(null);
  const statusRef = useRef(status);
  statusRef.current = status;

  const applySession = useCallback((data) => {
    setUser(data.user);
    setPermissions(data.permissions || null);
    setRbac(data.rbac || null);
    setStatus('authenticated');
    setLogoutReason(null);
    markAuthenticated();
  }, []);

  const clearSession = useCallback((reason) => {
    setUser(null);
    setPermissions(null);
    setRbac(null);
    setStatus('unauthenticated');
    if (reason) setLogoutReason(reason);
  }, []);

  // Hydrate from the httpOnly cookies. The api client already performs ONE refresh +
  // retry on 401, so a 401 that reaches us here is conclusive. Any other failure
  // (5xx / network) is NOT a sign-out: keep an existing session, or show a retry
  // screen on first load — never bounce the user to /login for an unrelated error.
  const refreshSession = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      applySession(data);
      return data.user;
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) {
        authDebug('session check conclusive: unauthenticated');
        clearSession(statusRef.current === 'authenticated' ? 'session_expired' : null);
        return null;
      }
      authDebug('session check inconclusive (kept current state)', code ?? 'network');
      if (statusRef.current !== 'authenticated') setStatus('error');
      return statusRef.current === 'authenticated' ? undefined : null;
    }
  }, [applySession, clearSession]);

  useEffect(() => { refreshSession(); }, [refreshSession]);

  // The ONE logout signal from the api client (refresh failed after a 401). Only surface a
  // "session expired" reason when someone was actually signed in — the very first
  // hydration on the login page also ends here for anonymous visitors.
  useEffect(() => {
    const onLogout = (e) => {
      authDebug('logout event received', e.detail);
      clearSession(statusRef.current === 'authenticated' ? (e.detail?.reason || 'session_expired') : null);
    };
    window.addEventListener(AUTH_LOGOUT_EVENT, onLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, onLogout);
  }, [clearSession]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    applySession(data);
    return data.user;
  }, [applySession]);

  const logout = useCallback(() => {
    api.post('/auth/logout', null, { _skipAuth: true }).catch(() => {});
    clearSession('user_logout');
  }, [clearSession]);

  const retrySession = useCallback(async () => {
    setStatus('loading');
    await refreshAccessToken();      // harmless if the cookie is still valid
    return refreshSession();
  }, [refreshSession]);

  // Derived access flags — one memo, driven by the user object + server rbac summary
  const derived = useMemo(() => deriveAccess(user, rbac), [user, rbac]);

  const hasPerm = useCallback(
    (module, action) => checkPerm({ user, permissions, isSuperAdmin: derived.isSuperAdmin }, module, action),
    [user, permissions, derived]
  );

  const canFeature = useCallback(
    (moduleKey, featureKey, action = 'view') =>
      checkFeature({ user, rbac, isSuperAdmin: derived.isSuperAdmin }, moduleKey, featureKey, action),
    [user, rbac, derived]
  );

  /** True when the user holds `action` on ANY feature of the module (used for app-tile visibility). */
  const canModule = useCallback((moduleKey, action = 'view') => {
    if (!user) return false;
    if (derived.isSuperAdmin) return true;
    const prefix = `${moduleKey}.`;
    return Object.entries(rbac?.features || {}).some(([k, acts]) => k.startsWith(prefix) && acts.includes(action));
  }, [user, rbac, derived]);

  const actions = useMemo(() => ({ login, logout, refreshSession, retrySession }), [login, logout, refreshSession, retrySession]);

  const value = useMemo(
    () => ({
      user: status === 'authenticated' ? user : null,
      status,
      loading: status === 'loading',
      sessionError: status === 'error',
      logoutReason,
      ...actions,
      hasPerm,
      canFeature,
      canModule,
      permissions,
      rbac,
      mustChangePassword: Boolean(status === 'authenticated' && user && (user.must_change_password || rbac?.mustChangePassword)),
      ...derived,
    }),
    [status, user, logoutReason, actions, hasPerm, canFeature, canModule, permissions, rbac, derived]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
